import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../styles/AdminDashboard.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const ADMIN_TOKEN_KEY = 'einv_admin_token';
const ADMIN_NOTICE_KEY = 'einv_admin_notice';
const ADMIN_APP_BASE_PATH = '/admin/invitations';
const ADMIN_API_BASE_PATH = '/admin/invitations';

function getApi(path) {
  return `${API_BASE_URL}${path}`;
}

export default function AdminInvitationEditor() {
  const { invitationId } = useParams();
  const navigate = useNavigate();

  const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState({
    category: '',
    previewPath: '',
    sourcePath: ''
  });
  const [form, setForm] = useState({
    title: '',
    rsvpEmail: '',
    eventDate: ''
  });

  function redirectToLogin(message = 'Your admin session expired. Please sign in again.') {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    sessionStorage.setItem(ADMIN_NOTICE_KEY, message);
    setToken('');
    navigate(ADMIN_APP_BASE_PATH, { replace: true });
  }

  async function readAdminResponse(res, fallbackMessage) {
    const json = await res.json().catch(() => ({}));
    if (res.status === 401) {
      redirectToLogin();
      return null;
    }
    if (!res.ok) {
      throw new Error(json.message || fallbackMessage);
    }
    return json;
  }

  useEffect(() => {
    if (!token) {
      navigate(ADMIN_APP_BASE_PATH, { replace: true });
      return;
    }

    async function loadInvitation() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(getApi(`${ADMIN_API_BASE_PATH}/${encodeURIComponent(invitationId || '')}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await readAdminResponse(res, 'Failed to load invitation.');
        if (!json) return;

        setForm({
          title: json.row?.title || '',
          rsvpEmail: json.row?.rsvpEmail || '',
          eventDate: json.row?.eventDate || ''
        });
        setDetails({
          category: json.row?.categoryLabel || json.row?.category || '',
          previewPath: json.row?.previewPath || '',
          sourcePath: json.row?.sourcePath || ''
        });
      } catch (err) {
        setError(err.message || 'Failed to load invitation.');
      } finally {
        setLoading(false);
      }
    }

    loadInvitation();
  }, [invitationId, navigate, token]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function onSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const res = await fetch(getApi(`${ADMIN_API_BASE_PATH}/${encodeURIComponent(invitationId || '')}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: form.title.trim(),
          rsvpEmail: form.rsvpEmail.trim().toLowerCase(),
          eventDate: form.eventDate || null
        })
      });
      const json = await readAdminResponse(res, 'Failed to save invitation.');
      if (!json) return;
      setMessage('Invitation saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to save invitation.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="adm-root adm-shell">
      <div className="adm-main">
        {/* Mobile top bar */}
        <header className="adm-topbar">
          <span className="adm-topbar-brand">E-Invitation Admin</span>
          <Link to={ADMIN_APP_BASE_PATH} className="adm-btn adm-btn-secondary adm-btn-sm" style={{ textDecoration: 'none', color: '#fff', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
            ← Back
          </Link>
        </header>

        <div className="adm-editor-page">
          {/* Back link */}
          <Link to={ADMIN_APP_BASE_PATH} className="adm-back-link">
            ← Back to Dashboard
          </Link>

          {/* Page header */}
          <div className="adm-page-header">
            <div>
              <p className="adm-page-eyebrow">Invitation Admin</p>
              <h1 className="adm-page-title">Edit Invitation</h1>
              <p className="adm-page-description" style={{ fontFamily: 'var(--adm-font-ui)', fontSize: '13px' }}>
                ID: <code style={{ background: '#f0ebe4', padding: '2px 7px', borderRadius: '5px', fontSize: '12px' }}>{invitationId}</code>
              </p>
            </div>
          </div>

          {loading ? (
            <div className="adm-section">
              <div className="adm-section-body" style={{ color: 'var(--adm-text-muted)', fontFamily: 'var(--adm-font-ui)', textAlign: 'center', padding: '40px' }}>
                Loading invitation…
              </div>
            </div>
          ) : (
            <>
              {/* Meta info card */}
              {(details.category || details.sourcePath || details.previewPath) && (
                <div className="adm-meta-card" style={{ marginBottom: '20px' }}>
                  {details.category && (
                    <div className="adm-meta-row">
                      <span className="adm-meta-key">Category:</span>
                      <span className="adm-table-badge">{details.category}</span>
                    </div>
                  )}
                  {details.sourcePath && (
                    <div className="adm-meta-row">
                      <span className="adm-meta-key">JSX File:</span>
                      <code style={{ fontFamily: 'monospace', fontSize: '12px', background: '#f0ebe4', padding: '2px 7px', borderRadius: '4px' }}>{details.sourcePath}</code>
                    </div>
                  )}
                  {details.previewPath && (
                    <div className="adm-meta-row">
                      <span className="adm-meta-key">Preview Route:</span>
                      <a
                        href={details.previewPath}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: 'var(--adm-primary)', fontFamily: 'monospace', fontSize: '12px', textDecoration: 'underline' }}
                      >
                        {details.previewPath} ↗
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Edit form */}
              <form onSubmit={onSave} className="adm-editor-form">
                <div className="adm-field">
                  <label className="adm-label" htmlFor="editor-title">Invitation Title</label>
                  <input
                    id="editor-title"
                    className="adm-input"
                    name="title"
                    type="text"
                    value={form.title}
                    onChange={onChange}
                    placeholder="e.g. Juan & Maria's Wedding"
                    required
                  />
                </div>

                <div className="adm-field">
                  <label className="adm-label" htmlFor="editor-email">RSVP Email</label>
                  <input
                    id="editor-email"
                    className="adm-input"
                    name="rsvpEmail"
                    type="email"
                    value={form.rsvpEmail}
                    onChange={onChange}
                    placeholder="event@domain.com"
                  />
                  <p className="adm-helper">RSVPs submitted by guests will be sent to this address.</p>
                </div>

                <div className="adm-field">
                  <label className="adm-label" htmlFor="editor-event-date">Event Date</label>
                  <input
                    id="editor-event-date"
                    className="adm-input"
                    name="eventDate"
                    type="date"
                    value={form.eventDate}
                    onChange={onChange}
                  />
                  <p className="adm-helper">RSVPs will automatically close after this date. Leave blank to keep RSVP open indefinitely.</p>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', paddingTop: '4px' }}>
                  <button type="submit" disabled={saving} className="adm-btn adm-btn-primary">
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <Link to={ADMIN_APP_BASE_PATH} className="adm-btn adm-btn-secondary">
                    Cancel
                  </Link>
                </div>
              </form>
            </>
          )}

          {error && <div className="adm-alert adm-alert-error" style={{ marginTop: '16px' }}>{error}</div>}
          {message && <div className="adm-alert adm-alert-success" style={{ marginTop: '16px' }}>{message}</div>}
        </div>
      </div>
    </div>
  );
}
